import React, { useState, useMemo, useEffect } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';

const OutstandingRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'PENDING',
    user_type: '',
    date_from: '',
    date_to: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionRemarks, setRejectionRemarks] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOutstandingRequests();
    }, searchQuery ? 500 : 0); // Debounce search

    return () => clearTimeout(timer);
  }, [currentPage, rowsPerPage, searchQuery, filters]);

  const fetchOutstandingRequests = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        mobile: searchQuery,
        ...filters
      };

      const response = await distributorService.getOutstandingRequests(params);
      
      if (response.success) {
        // Handle API response structure
        let requestsData = [];
        let count = 0;
        
        if (response.data && Array.isArray(response.data.results)) {
          requestsData = response.data.results;
          count = response.data.count || requestsData.length;
        } else if (Array.isArray(response.data)) {
          requestsData = response.data;
          count = requestsData.length;
        }
        
        setRequests(requestsData);
        setTotalCount(count);
      } else {
        toast.error(response.error || 'Failed to fetch outstanding requests');
        setRequests([]); // Ensure requests is always an array
        setTotalCount(0);
      }
    } catch (error) {
      toast.error('Failed to fetch outstanding requests');
      console.error('Error fetching requests:', error);
      setRequests([]); // Ensure requests is always an array on error
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowApproveModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setRejectionRemarks('');
    setShowRejectModal(true);
  };

  const confirmApprove = async () => {
    if (!selectedRequest) return;
    
    setLoading(true);
    try {
      const response = await distributorService.approveOutstandingRequest(selectedRequest.id);
      
      if (response.success) {
        toast.success(response.message);
        setShowApproveModal(false);
        setSelectedRequest(null);
        fetchOutstandingRequests(); // Refresh the list
      } else {
        toast.error(response.error || 'Failed to approve request');
      }
    } catch (error) {
      toast.error('Failed to approve request');
      console.error('Error approving request:', error);
    } finally {
      setLoading(false);
    }
  };

  const confirmReject = async () => {
    if (!selectedRequest || !rejectionRemarks.trim()) {
      toast.error('Please provide rejection remarks');
      return;
    }
    
    setLoading(true);
    try {
      const response = await distributorService.rejectOutstandingRequest(
        selectedRequest.id, 
        rejectionRemarks
      );
      
      if (response.success) {
        toast.success(response.message);
        setShowRejectModal(false);
        setSelectedRequest(null);
        setRejectionRemarks('');
        fetchOutstandingRequests(); // Refresh the list
      } else {
        toast.error(response.error || 'Failed to reject request');
      }
    } catch (error) {
      toast.error('Failed to reject request');
      console.error('Error rejecting request:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        mobile: searchQuery
      };

      const response = await distributorService.exportOutstandingRequests(params);
      
      if (response.success) {
        // Create blob and download file
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Outstanding requests exported successfully');
      } else {
        toast.error('Failed to export outstanding requests');
      }
    } catch (error) {
      toast.error('Failed to export outstanding requests');
      console.error('Error exporting requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge bg="warning" text="dark">Pending</Badge>;
      case 'APPROVED':
        return <Badge bg="success">Approved</Badge>;
      case 'REJECTED':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getUserTypeBadge = (userType) => {
    switch (userType) {
      case 'retailer':
        return <Badge bg="info">Retailer</Badge>;
      case 'fos':
        return <Badge bg="primary">FOS</Badge>;
      default:
        return <Badge bg="secondary">{userType}</Badge>;
    }
  };

  const getPaymentModeBadge = (paymentMode) => {
    switch (paymentMode) {
      case 'CASH':
        return <Badge bg="success">Cash</Badge>;
      case 'UPI':
        return <Badge bg="primary">UPI</Badge>;
      case 'DEPOSIT':
        return <Badge bg="info">Bank Deposit</Badge>;
      case 'CREDIT':
        return <Badge bg="warning" text="dark">Credit</Badge>;
      default:
        return <Badge bg="secondary">{paymentMode}</Badge>;
    }
  };

  const getTransactionTypeBadge = (txnType) => {
    switch (txnType) {
      case 'PAID_FOR_OUTSTANDING':
        return <Badge bg="danger" className="text-nowrap">Outstanding Payment</Badge>;
      case 'PAID_FOR_TOPUP':
        return <Badge bg="success" className="text-nowrap">Topup Payment</Badge>;
      case 'TOPUP':
        return <Badge bg="primary" className="text-nowrap">Topup</Badge>;
      case 'PAIED':
        return <Badge bg="warning" text="dark" className="text-nowrap">Paid</Badge>;
      default:
        return <Badge bg="secondary" className="text-nowrap">{txnType}</Badge>;
    }
  };

  const totalPages = totalCount > 0 ? Math.ceil(totalCount / rowsPerPage) : 1;

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-clipboard-check me-2"></i>
          Outstanding Requests
        </h5>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={fetchOutstandingRequests} disabled={loading}>
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </Button>
          <Button variant="success" onClick={exportToExcel} disabled={loading}>
            <i className="fas fa-file-excel me-2"></i>
            {loading ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            value={filters.user_type}
            onChange={(e) => handleFilterChange('user_type', e.target.value)}
          >
            <option value="">All Users</option>
            <option value="retailer">Retailer</option>
            <option value="fos">FOS</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            placeholder="From Date"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            placeholder="To Date"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
          />
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>ID</th>
              <th>Amount</th>
              <th>UTR Number</th>
              <th>User Details</th>
              <th>Transaction Type</th>
              <th>Payment Mode</th>
              <th>Status</th>
              <th>Remarks</th>
              <th>Created Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : !Array.isArray(requests) || requests.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-4 text-muted">
                  <div className="d-flex flex-column align-items-center">
                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Outstanding Requests Found</h5>
                    <p className="text-muted mb-0">
                      {searchQuery || Object.values(filters).some(f => f) 
                        ? 'Try adjusting your search or filter criteria'
                        : 'There are no outstanding requests to display'
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              requests.map((request) => (
                <tr key={request.id}>
                  <td>
                    {request.status === 'PENDING' ? (
                      <div className="d-flex gap-1">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleApprove(request)}
                          title="Approve Request"
                        >
                          <i className="fas fa-check"></i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleReject(request)}
                          title="Reject Request"
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    ) : (
                      <Badge bg="secondary">Processed</Badge>
                    )}
                  </td>
                  <td>#{request.id}</td>
                  <td>
                    <strong className="text-primary">₹{parseFloat(request.amount).toLocaleString()}</strong>
                  </td>
                  <td>
                    <code className="bg-light p-1 rounded">{request.utr_number}</code>
                  </td>
                  <td>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        {getUserTypeBadge(request.user_type)}
                        <strong>{request.user_name || 'N/A'}</strong>
                      </div>
                      <small className="text-muted d-block">
                        <i className="fas fa-phone me-1"></i>
                        {request.user_mobile || 'N/A'}
                      </small>
                      <small className="text-muted d-block">
                        <i className="fas fa-user me-1"></i>
                        ID: {request.user || 'N/A'}
                      </small>
                    </div>
                  </td>
                  <td>{getTransactionTypeBadge(request.txn_type)}</td>
                  <td>{getPaymentModeBadge(request.payment_mode)}</td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>
                    <div className="text-truncate" style={{ maxWidth: '200px' }}>
                      {request.remarks ? (
                        <span title={request.remarks}>{request.remarks}</span>
                      ) : (
                        <span className="text-muted">No remarks</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      <small className="d-block">{request.created_at}</small>
                      <small className="text-muted">{request.created_time?.split('.')[0]}</small>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {Array.isArray(requests) && requests.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {(() => {
            const pages = [];
            const maxVisiblePages = 5;
            
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
            
            if (endPage - startPage < maxVisiblePages - 1) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }
            
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <Pagination.Item
                  key={i}
                  active={i === currentPage}
                  onClick={() => setCurrentPage(i)}
                >
                  {i}
                </Pagination.Item>
              );
            }
            
            return pages;
          })()}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Approve Confirmation Modal */}
      <Modal show={showApproveModal} onHide={() => setShowApproveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Approve Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <p>Are you sure you want to approve this outstanding request?</p>
              <div className="bg-light p-3 rounded">
                <div><strong>Amount:</strong> ₹{parseFloat(selectedRequest.amount).toLocaleString()}</div>
                <div><strong>UTR:</strong> {selectedRequest.utr_number}</div>
                <div><strong>User:</strong> {selectedRequest.user_name} ({selectedRequest.user_mobile})</div>
                <div><strong>Transaction Type:</strong> {selectedRequest.txn_type}</div>
                <div><strong>Payment Mode:</strong> {selectedRequest.payment_mode}</div>
                {selectedRequest.remarks && (
                  <div><strong>Remarks:</strong> {selectedRequest.remarks}</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApproveModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={confirmApprove} disabled={loading}>
            <i className="fas fa-check me-2"></i>
            {loading ? 'Approving...' : 'Approve Request'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div>
              <div className="mb-3">
                <div className="bg-light p-3 rounded">
                  <div><strong>Amount:</strong> ₹{parseFloat(selectedRequest.amount).toLocaleString()}</div>
                  <div><strong>UTR:</strong> {selectedRequest.utr_number}</div>
                  <div><strong>User:</strong> {selectedRequest.user_name} ({selectedRequest.user_mobile})</div>
                  <div><strong>Transaction Type:</strong> {selectedRequest.txn_type}</div>
                  {selectedRequest.remarks && (
                    <div><strong>Remarks:</strong> {selectedRequest.remarks}</div>
                  )}
                </div>
              </div>
              <Form.Group>
                <Form.Label>Rejection Remarks *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={rejectionRemarks}
                  onChange={(e) => setRejectionRemarks(e.target.value)}
                  placeholder="Please provide the reason for rejection..."
                  maxLength={1000}
                  required
                />
                <Form.Text className="text-muted">
                  {rejectionRemarks.length}/1000 characters
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmReject} 
            disabled={loading || !rejectionRemarks.trim()}
          >
            <i className="fas fa-times me-2"></i>
            {loading ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default OutstandingRequests;